// src/pages/AdminStaffInfoPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';
import { canAssignStaff, getRoleColorClass, shouldShowAssignButton, getAssignableRoles } from '../utils/permissions';
import * as XLSX from 'xlsx';

// --- 사용자 상세 정보 모달 컴포넌트 ---
const UserDetailModal = ({ user, onClose, onDeleteMembership, currentUser }) => {
  const getActiveBranches = (member) => {
    // ✅ ADMIN은 특별 표시 (모든 지부 접근 권한이지만 특정 지부에 소속되지 않음)
    if (member?.role === 'ADMIN') {
      return ['ALL (System-wide access)'];
    }
    
    const set = new Set();
    const str = (member?.membership || '').trim();
    if (str) set.add(str);
    if (Array.isArray(member?.memberships)) {
      for (const um of member.memberships) {
        const b = (um?.branchName || '').trim();
        const status = (um?.status || '').toUpperCase();
        if (b && (status === 'ACTIVE' || status === '')) set.add(b);
      }
    }
    return Array.from(set);
  };

  const branches = getActiveBranches(user);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white p-6 sm:p-8 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl sm:text-2xl font-bold">{user?.name || 'Unknown User'}</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>
        
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-1 gap-3">
            {/* Email */}
            <div className="p-3 bg-gray-50 rounded">
              <p><strong>Email:</strong> {user?.email || 'N/A'}</p>
            </div>
            
            {/* Role */}
            <div className="p-3 bg-gray-50 rounded">
              <p><strong>Role:</strong> 
                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getRoleColorClass(user?.role)}`}>
                  {user?.role || 'N/A'}
                </span>
              </p>
            </div>
            
            {/* Branch */}
            <div className="p-3 bg-gray-50 rounded">
              <p><strong>Branch:</strong> {branches.join(', ') || 'N/A'}</p>
            </div>
            
            {/* Student ID */}
            <div className="p-3 bg-gray-50 rounded">
              <p><strong>Student ID:</strong> {user?.studentId || 'N/A'}</p>
            </div>
            
            {/* Phone */}
            <div className="p-3 bg-gray-50 rounded">
              <p><strong>Phone:</strong> {user?.phone || 'N/A'}</p>
            </div>
            
            {/* Major */}
            <div className="p-3 bg-gray-50 rounded">
              <p><strong>Major:</strong> {user?.major || 'N/A'}</p>
            </div>
            
            {/* Nationality */}
            <div className="p-3 bg-gray-50 rounded">
              <p><strong>Nationality:</strong> {user?.nationality === 'N/A' || !user?.nationality ? 'Taiwan' : user.nationality}</p>
            </div>
            
            {/* Payment */}
            <div className="p-3 bg-gray-50 rounded">
              <p><strong>Payment:</strong> {user?.paymentMethod ? `${user.paymentMethod === 'transfer' ? `Transfer (${user.bankLast5 || 'N/A'})` : 'Cash'} - ${user.amount || 'N/A'} NTD` : 'N/A'}</p>
            </div>
            
            {/* Additional Info */}
            {user?.affiliation && (
              <div className="p-3 bg-gray-50 rounded">
                <p><strong>Affiliation:</strong> {user.affiliation}</p>
              </div>
            )}
            
            {user?.bio && (
              <div className="p-3 bg-gray-50 rounded">
                <p><strong>Bio:</strong></p>
                <p className="mt-1 text-gray-700">{user.bio}</p>
              </div>
            )}
            
            {user?.interests && (
              <div className="p-3 bg-gray-50 rounded">
                <p><strong>Interests:</strong> {user.interests}</p>
              </div>
            )}
            
            {user?.spokenLanguages && (
              <div className="p-3 bg-gray-50 rounded">
                <p><strong>Spoken Languages:</strong> {user.spokenLanguages}</p>
              </div>
            )}
            
            {user?.desiredLanguages && (
              <div className="p-3 bg-gray-50 rounded">
                <p><strong>Desired Languages:</strong> {user.desiredLanguages}</p>
              </div>
            )}
            
            {user?.foodAllergies && (
              <div className="p-3 bg-gray-50 rounded">
                <p><strong>Dietary Preferences:</strong> {user.foodAllergies}</p>
              </div>
            )}
            
            {user?.howDidYouHear && (
              <div className="p-3 bg-gray-50 rounded">
                <p><strong>How did you hear about SLAM:</strong> {user.howDidYouHear}</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="mt-6 space-y-2">
          <button 
            onClick={onClose} 
            className="w-full py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
          
          {onDeleteMembership && branches.length > 0 && currentUser?.role === 'PRESIDENT' && (
            <button 
              onClick={() => {
                if (window.confirm(`Are you sure you want to delete ${user?.name || 'this user'}'s membership?`)) {
                  // 첫 번째 branch를 기준으로 삭제 (또는 사용자가 선택하도록 개선 가능)
                  onDeleteMembership(user.id, branches[0]);
                }
              }} 
              className="w-full py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Delete Membership
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function AdminStaffInfoPage() {
  const { user } = useUser();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignModal, setAssignModal] = useState({ open: false, userId: null, email: '', currentRole: null });
  const [assignRole, setAssignRole] = useState('STAFF');
  const [assignBranch, setAssignBranch] = useState('TAIPEI');
  
  // ✅ 지부 선택 모달 상태
  const [branchSelectionModal, setBranchSelectionModal] = useState({ 
    open: false, 
    userId: null, 
    userName: '', 
    userMemberships: [] 
  });
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // 선택된 사용자 상세 정보용

  // 사용자의 실제 팀 추출 함수
  const getUserDepartment = (user) => {
    const affiliation = user?.affiliation || user?.userProfile?.affiliation || '';
    if (affiliation === 'General Affairs') return 'GA';
    if (affiliation === 'Public Relations') return 'PR';
    if (affiliation === 'Event Planning') return 'EP';
    // 기존 형식도 지원
    if (affiliation.includes('GA팀') || affiliation.includes('GA')) return 'GA';
    if (affiliation.includes('PR팀') || affiliation.includes('PR')) return 'PR';
    if (affiliation.includes('EP팀') || affiliation.includes('EP')) return 'EP';
    return 'Not specified';
  };

  // Filters / Search / Sort
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [branchFilter, setBranchFilter] = useState('ALL');
  const [nationalityFilter, setNationalityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL'); // Status 필터 추가
  const [teamFilter, setTeamFilter] = useState('ALL'); // 팀별 필터 추가
  const [sortKey, setSortKey] = useState('name');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  
  // Sorting
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Role info modal
  const [showRoleInfo, setShowRoleInfo] = useState(false);
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  
  // Bulk assignment
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkAssignRole, setBulkAssignRole] = useState('STAFF');
  const [bulkAssignBranch, setBulkAssignBranch] = useState('TAIPEI');

  // 정렬 함수
  const handleSort = (key) => {
    if (sortKey === key) {
      // 같은 키면 방향 토글
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 다른 키면 새로운 키로 오름차순 정렬
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Clear all filters 함수
  const handleClearFilters = () => {
    setSearch('');
    setRoleFilter('ALL');
    setBranchFilter('ALL');
    setNationalityFilter('ALL');
    setStatusFilter('ALL');
    setTeamFilter('ALL');
    setSortKey('name');
    setSortDirection('asc');
    setCurrentPage(1);
  };

  // 정렬 아이콘 반환
  const getSortIcon = (key) => {
    if (sortKey !== key) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Role 정보 팝업
  const RoleInfoModal = () => (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">👥 Role Information</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">ADMIN</span>
            <span className="text-sm">System administrator - Full access</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">PRESIDENT</span>
            <span className="text-sm">Branch president - Staff assignment authority</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">LEADER</span>
            <span className="text-sm">Team leader - Limited assignment authority</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">STAFF</span>
            <span className="text-sm">General staff (GA, PR, EP, etc.)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">MEMBER</span>
            <span className="text-sm">Active member - Can join events</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">GUEST</span>
            <span className="text-sm">Guest - Needs membership application</span>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h3 className="font-medium text-sm mb-2">📝 Note: Role Information</h3>
          <div className="text-xs text-gray-600 space-y-1">
            <p>Click on role tags in the table to see detailed membership status (NON_MEMBER/ACTIVE_MEMBER/EXPIRED_MEMBER) and other information.</p>
            <p>Role tags above can be clicked to filter users by their permission level.</p>
          </div>
        </div>
        <div className="mt-6 text-center">
          <button 
            onClick={() => setShowRoleInfo(false)} 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200); // iPad Pro까지 카드 뷰 사용
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const canAssignStaffPermission = useMemo(() => {
    return canAssignStaff(user?.role);
  }, [user?.role]);


  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      try {
        const res = await axios.get('/api/admin/users');
        setMembers(res.data);
      } catch (e) {
        alert('멤버 목록을 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);
  
  // Bulk assignment functions
  const canShowBulkAssign = user?.role === 'ADMIN' || user?.role === 'PRESIDENT';
  
  const handleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };
  
  const handleSelectAll = () => {
    if (selectedUsers.size === filteredMembers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredMembers.map(m => m.id)));
    }
  };
  
  const clearSelection = () => {
    setSelectedUsers(new Set());
  };
  
  // 팀별 필터 함수
  const handleTeamFilter = (team) => {
    try {
      setTeamFilter(team);
      setRoleFilter('STAFF'); // STAFF 필터도 적용
      setCurrentPage(1); // 페이지 리셋
    } catch (error) {
      // Silent fail
    }
  };
  
  // 팀 필터 리셋 함수
  const resetTeamFilter = () => {
    setTeamFilter('ALL');
    setRoleFilter('ALL');
    setCurrentPage(1);
  };
  
  // Role 필터 변경 함수 (팀 필터 초기화 포함)
  const handleRoleFilterChange = (newRole) => {
    setRoleFilter(newRole);
    setTeamFilter('ALL'); // 팀 필터 초기화
    setCurrentPage(1);
  };
  
  // 역할 태그 클릭 함수 (팀 필터 초기화 포함)
  const handleRoleTagClick = (role) => {
    if (role === 'NON_MEMBER' || role === 'ACTIVE_MEMBER' || role === 'EXPIRED_MEMBER') {
      // Status 기준으로 필터링
      setStatusFilter(role);
      setRoleFilter('ALL'); // roleFilter 초기화
    } else {
      // 다른 역할들은 role 기준으로 필터링
      setRoleFilter(role);
      setStatusFilter('ALL'); // statusFilter 초기화
    }
    setTeamFilter('ALL'); // 팀 필터 초기화
    setCurrentPage(1);
  };
  
  const handleBulkAssign = async () => {
    if (selectedUsers.size === 0) {
      alert('Please select users to assign roles.');
      return;
    }
    
    try {
      const promises = Array.from(selectedUsers).map(userId => {
        return axios.post('/api/admin/users/assign-staff', {
          userId: userId,
          targetRole: bulkAssignRole,
          reason: `Bulk assigned as ${bulkAssignRole} by admin`,
          branch: bulkAssignRole !== 'GUEST' ? bulkAssignBranch : undefined
        });
      });
      
      await Promise.all(promises);
      alert(`Successfully assigned ${selectedUsers.size} users to ${bulkAssignRole} role.`);
      setShowBulkAssign(false);
      clearSelection();
      fetchMembers(); // Refresh list
    } catch (error) {
      alert('Failed to assign roles. Please try again.');
    }
  };

  // Helper: derive active branches for a member from both string field and memberships collection
  const getActiveBranches = (m) => {
    // ✅ ADMIN은 특별 처리: 'ALL'일 때만 포함, 특정 지부 선택 시 제외
    if (m?.role === 'ADMIN') {
      return []; // ADMIN은 빈 배열 반환 (특정 지부에 속하지 않음)
    }
    
    const set = new Set();
    const str = (m?.membership || '').trim();
    if (str) set.add(str);
    if (Array.isArray(m?.memberships)) {
      for (const um of m.memberships) {
        const b = (um?.branchName || '').trim();
        const status = (um?.status || '').toUpperCase();
        if (b && (status === 'ACTIVE' || status === '')) set.add(b);
      }
    }
    return Array.from(set);
  };

  // Distinct branches for filter
  const branches = useMemo(() => {
    const set = new Set();
    for (const m of members || []) {
      for (const b of getActiveBranches(m)) set.add(b);
    }
    return ['ALL', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [members]);

  // Distinct nationalities for filter
  const nationalities = useMemo(() => {
    const set = new Set();
    for (const m of members || []) {
      if (m?.nationality && m.nationality.trim()) {
        set.add(m.nationality.trim());
      }
    }
    const sortedList = Array.from(set).sort((a, b) => a.localeCompare(b));
    // Add Taiwan if not already present (for N/A cases)
    if (!sortedList.includes('Taiwan')) {
      sortedList.push('Taiwan');
      sortedList.sort((a, b) => a.localeCompare(b));
    }
    return ['ALL', 'NON_TAIWAN', ...sortedList];
  }, [members]);

  // Role options - 모든 역할 포함
  const roleOptions = ['ALL', 'ADMIN', 'PRESIDENT', 'LEADER', 'STAFF', 'MEMBER', 'GUEST'];

  // Status options - 제거됨
  // const statusOptions = ['ALL', 'NON_MEMBER', 'ACTIVE_MEMBER', 'EXPIRED_MEMBER'];

  // Derived list
  const filteredMembers = useMemo(() => {
    try {
      const q = search.trim().toLowerCase();
      let list = [...(members || [])];

      if (q) {
        list = list.filter(m => 
          (m?.name || '').toLowerCase().includes(q) ||
          (m?.email || '').toLowerCase().includes(q)
        );
      }

    if (roleFilter !== 'ALL') {
      if (roleFilter === 'STAFF' || roleFilter === 'ALL_STAFF') {
        // STAFF 선택 시 ALL_STAFF처럼 작동 (PRESIDENT, LEADER, STAFF 모두 포함)
        list = list.filter(m => ['PRESIDENT', 'LEADER', 'STAFF'].includes((m.role || '').toUpperCase()));
      } else if (roleFilter === 'ADMIN') {
        // ADMIN 필터: ADMIN만 표시 (지부 필터와 관계없이)
        list = list.filter(m => (m.role || '').toUpperCase() === 'ADMIN');
      } else {
        list = list.filter(m => (m.role || '').toUpperCase() === roleFilter);
      }
    }

    if (branchFilter !== 'ALL') {
      list = list.filter(m => {
        // ✅ ADMIN은 특정 지부 선택 시 제외
        if (m?.role === 'ADMIN') return false;
        return getActiveBranches(m).includes(branchFilter);
      });
    }

    if (nationalityFilter !== 'ALL') {
      if (nationalityFilter === 'NON_TAIWAN') {
        list = list.filter(m => {
          const displayNationality = m.nationality === 'N/A' || !m.nationality ? 'Taiwan' : m.nationality;
          return displayNationality !== 'Taiwan';
        });
      } else {
        list = list.filter(m => {
          const displayNationality = m.nationality === 'N/A' || !m.nationality ? 'Taiwan' : m.nationality;
          return displayNationality === nationalityFilter;
        });
      }
    }

    // Status 필터 추가
    if (statusFilter !== 'ALL') {
      list = list.filter(m => (m.status || '').toUpperCase() === statusFilter);
    }
    
    // 팀별 필터 추가
    if (teamFilter !== 'ALL') {
      list = list.filter(m => {
        try {
          const affiliation = m.affiliation || m.userProfile?.affiliation || '';
          // 팀명 매핑
          const teamMap = {
            'GA': 'General Affairs',
            'PR': 'Public Relations',
            'EP': 'Event Planning'
          };
          const hasTeam = affiliation === teamMap[teamFilter];
          return hasTeam;
        } catch (error) {
          return false;
        }
      });
    }

    const by = (v) => (v == null ? '' : String(v));
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    if (sortKey === 'name') {
      list.sort((a, b) => by(a.name).localeCompare(by(b.name)) * direction);
    } else if (sortKey === 'email') {
      list.sort((a, b) => by(a.email).localeCompare(by(b.email)) * direction);
    } else if (sortKey === 'role') {
      // 역할 계층 순서: ADMIN, PRESIDENT, LEADER, STAFF, MEMBER, GUEST
      const roleOrder = {
        'ADMIN': 1,
        'PRESIDENT': 2,
        'LEADER': 3,
        'STAFF': 4,
        'MEMBER': 5,
        'GUEST': 6
      };
      list.sort((a, b) => {
        const orderA = roleOrder[a.role] || 999;
        const orderB = roleOrder[b.role] || 999;
        return (orderA - orderB) * direction;
      });
    } else if (sortKey === 'branch') {
      const firstBranch = (m) => getActiveBranches(m)[0] || '';
      list.sort((a, b) => by(firstBranch(a)).localeCompare(by(firstBranch(b))) * direction);
    } else if (sortKey === 'nationality') {
      list.sort((a, b) => by(a.nationality || 'Taiwan').localeCompare(by(b.nationality || 'Taiwan')) * direction);
    } else if (sortKey === 'joinDate') {
      list.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return (dateB - dateA) * direction; // 기본은 최신순
      });
    }

      return list;
    } catch (error) {
      return members || []; // 에러 시 원본 데이터 반환
    }
  }, [members, search, roleFilter, branchFilter, nationalityFilter, statusFilter, teamFilter, sortKey, sortDirection]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredMembers.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  // Reset to first page when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, roleFilter, branchFilter, nationalityFilter, statusFilter, teamFilter, pageSize, sortKey, sortDirection]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const roleCounts = useMemo(() => {
    try {
      const counts = { ADMIN: 0, PRESIDENT: 0, LEADER: 0, STAFF: 0, ALL_STAFF: 0, GA: 0, PR: 0, EP: 0 };
      
      if (!members || !Array.isArray(members)) {
        return counts;
      }
      
      // ✅ 지부 필터 적용: branchFilter가 'ALL'이 아니면 해당 지부 멤버만 카운트 (ADMIN 제외)
      const membersToCount = branchFilter === 'ALL' 
        ? members 
        : members.filter(m => {
            // ADMIN은 특정 지부 선택 시 제외
            if (m?.role === 'ADMIN') return false;
            return getActiveBranches(m).includes(branchFilter);
          });
      
      for (const m of membersToCount) {
        const r = (m.role || '').toUpperCase();
        
        // Role counts
        if (counts[r] != null) counts[r] += 1;
        if (['PRESIDENT', 'LEADER', 'STAFF'].includes(r)) {
          counts.ALL_STAFF += 1;
        }
        
        // 부서별 카운트 - affiliation 필드에서 추출
        if (r === 'STAFF' || r === 'LEADER') {
          const affiliation = m.affiliation || m.userProfile?.affiliation || '';
          if (affiliation === 'General Affairs') {
            counts.GA += 1;
          } else if (affiliation === 'Public Relations') {
            counts.PR += 1;
          } else if (affiliation === 'Event Planning') {
            counts.EP += 1;
          } else {
            // 기본값으로 GA에 할당
            counts.GA += 1;
          }
        }
      }
      return counts;
    } catch (error) {
      return { ADMIN: 0, PRESIDENT: 0, LEADER: 0, STAFF: 0, ALL_STAFF: 0, GA: 0, PR: 0, EP: 0 };
    }
  }, [members, branchFilter]);
  
  const statusCounts = useMemo(() => {
    try {
      const counts = { NON_MEMBER: 0, ACTIVE_MEMBER: 0, EXPIRED_MEMBER: 0 };
      
      if (!members || !Array.isArray(members)) {
        return counts;
      }
      
      // ✅ Status 카운트: NON_MEMBER는 항상 ALL 기준, ACTIVE/EXPIRED는 지부 필터 적용 (ADMIN 제외)
      for (const m of members) {
        const s = (m?.status || '').toUpperCase();
        
        if (s === 'NON_MEMBER') {
          // NON_MEMBER는 지부가 없으므로 항상 카운트
          counts.NON_MEMBER += 1;
        } else if (s === 'ACTIVE_MEMBER' || s === 'EXPIRED_MEMBER') {
          // ACTIVE/EXPIRED는 지부 필터 적용 (ADMIN 제외)
          if (branchFilter === 'ALL') {
            counts[s] += 1;
          } else {
            // 특정 지부 선택 시 ADMIN 제외
            if (m?.role !== 'ADMIN' && getActiveBranches(m).includes(branchFilter)) {
              counts[s] += 1;
            }
          }
        }
      }
      return counts;
    } catch (error) {
      return { NON_MEMBER: 0, ACTIVE_MEMBER: 0, EXPIRED_MEMBER: 0 };
    }
  }, [members, branchFilter]);

  const branchCounts = useMemo(() => {
    const counts = {};
    for (const m of members) {
      const bs = getActiveBranches(m);
      for (const b of bs) counts[b] = (counts[b] || 0) + 1;
    }
    return counts;
  }, [members]);

  const exportXLSX = () => {
    const header = [
      'Timeline',
      'Name',
      'Email',
      'Student ID',
      'Major',
      'Nationality',
      'Phone',
      'Payment Method',
      'Payment Amount',
      'Branch',
      'Events Joined',
      'Professional Status'
    ];
    const data = filteredMembers.map(m => [
      m.createdAt ? new Date(m.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown',
      m.name || '',
      m.email || '',
      m.studentId || '',
      m.major || '',
      m.nationality === 'N/A' || !m.nationality ? 'Taiwan' : (m.nationality || ''),
      m.phone || '',
      m.paymentMethod ? (m.paymentMethod === 'transfer' ? `Transfer (${m.bankLast5 || 'N/A'})` : 'Cash') : 'N/A',
      m.amount || 'N/A',
      getActiveBranches(m).join(', '),
      '0', // Events Joined - Member Info doesn't track this
      m.professionalStatus || ''
    ]);
    
    // 워크시트 생성
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    
    // 워크북 생성
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Staff Members');
    
    // 파일 다운로드
    XLSX.writeFile(wb, 'staff_members.xlsx');
  };

  const openAssign = (member) => {
    if (!shouldShowAssignButton(user?.role, member.role)) return alert('You do not have permission to assign roles.');
    if (user?.id === member.id) return alert('You cannot change your own role.');
    
    const assignableRoles = getAssignableRoles(user?.role, member.role);
    
    // 할당 가능한 역할이 없으면 경고
    if (assignableRoles.length === 0) {
      return alert(`${member.name} already has the highest role you can assign or no available roles to assign.`);
    }
    
    const defaultRole = assignableRoles.includes('STAFF') ? 'STAFF' : assignableRoles[0];
    setAssignModal({ open: true, userId: member.id, email: member.email, currentRole: member.role });
    setAssignRole(defaultRole);
    setAssignBranch(member.membership || 'TAIPEI'); // 현재 지부를 기본값으로 설정
  };

  // ✅ 지부별 quit 처리 함수
  const handleQuitStaff = async (member) => {
    try {
      // 사용자의 활성 멤버십 확인
      const activeMemberships = member.memberships?.filter(m => m.status === 'ACTIVE') || [];
      
      if (activeMemberships.length > 1) {
        // 2개 이상이면 선택 모달 표시
        setBranchSelectionModal({
          open: true,
          userId: member.id,
          userName: member.name,
          userMemberships: activeMemberships
        });
        setSelectedBranches(activeMemberships.map(m => m.branchName));
      } else if (activeMemberships.length === 1) {
        // 1개만 있으면 바로 quit
        const confirmQuit = window.confirm(
          `Are you sure you want to quit ${member.name} from ${activeMemberships[0].branchName}?`
        );
        if (confirmQuit) {
          await quitFromBranches(member.id, activeMemberships.map(m => m.branchName));
        }
      } else {
        alert('No active memberships found for this user.');
      }
    } catch (error) {
      alert('Error processing quit request.');
    }
  };

  // ✅ 지부별 quit API 호출
  const quitFromBranches = async (userId, branchNames) => {
    try {
      const response = await axios.post('/api/admin/users/quit-branches', null, {
        params: {
          userId: userId,
          branchNames: branchNames
        }
      });

      if (response.data.success) {
        alert(`Successfully quit from selected branches. Remaining branches: ${response.data.remainingBranches.join(', ') || 'None'}`);
        // 데이터 새로고침
        fetchMembers();
        setBranchSelectionModal({ open: false, userId: null, userName: '', userMemberships: [] });
      } else {
        alert('Failed to quit from branches: ' + response.data.error);
      }
    } catch (error) {
      alert('Error quitting from branches: ' + (error.response?.data?.error || error.message));
    }
  };

  // ✅ 지부 선택 모달 확인
  const confirmBranchSelection = async () => {
    if (selectedBranches.length === 0) {
      alert('Please select at least one branch to quit from.');
      return;
    }

    const confirmQuit = window.confirm(
      `Are you sure you want to quit ${branchSelectionModal.userName} from: ${selectedBranches.join(', ')}?`
    );
    
    if (confirmQuit) {
      await quitFromBranches(branchSelectionModal.userId, selectedBranches);
    }
  };

  const submitAssign = async () => {
    try {
      // 대상 사용자의 현재 역할 확인 (먼저 선언)
      const targetMember = filteredMembers.find(m => m.id === assignModal.userId);
      
      // ✅ GUEST로 변경하는 경우 지부별 quit 처리
      if (assignRole === 'GUEST') {
        const activeMemberships = targetMember?.memberships?.filter(m => m.status === 'ACTIVE') || [];
        
        if (activeMemberships.length > 1) {
          // 2개 이상이면 선택 모달 표시
          setAssignModal({ open: false, userId: null, email: '', currentRole: null });
          setBranchSelectionModal({
            open: true,
            userId: assignModal.userId,
            userName: targetMember.name,
            userMemberships: activeMemberships
          });
          setSelectedBranches(activeMemberships.map(m => m.branchName));
          return;
        } else if (activeMemberships.length === 1) {
          // 1개만 있으면 바로 quit
          const confirmQuit = window.confirm(
            `Are you sure you want to quit ${targetMember.name} from ${activeMemberships[0].branchName}?`
          );
          if (confirmQuit) {
            await quitFromBranches(assignModal.userId, activeMemberships.map(m => m.branchName));
            setAssignModal({ open: false, userId: null, email: '', currentRole: null });
            return;
          } else {
            return; // 취소된 경우
          }
        } else {
          alert('No active memberships found for this user.');
          return;
        }
      }
      
      // STAFF를 GUEST로 변경하는 경우 탈퇴 처리 경고 표시
      if (assignRole === 'GUEST') {
        const confirmed = window.confirm(
          `⚠️ WARNING: Are you sure you want to remove ${targetMember?.name} from staff?\n\n` +
          `This will:\n` +
          `• Remove all staff privileges\n` +
          `• Expire their current membership\n` +
          `• Change their role to GUEST\n` +
          `• They must rejoin membership to attend events\n\n` +
          `This action cannot be easily undone. Please confirm carefully.`
        );
        if (!confirmed) return;
      }
      
      // 다른 역할을 MEMBER로 변경하는 경우 일반 경고
      if (assignRole === 'MEMBER' && targetMember?.role !== 'STAFF') {
        const confirmed = window.confirm(
          `⚠️ WARNING: Are you sure you want to change ${targetMember?.name} to Member?\n\n` +
          `This will remove their current privileges.\n\n` +
          `Please confirm carefully.`
        );
        if (!confirmed) return;
      }
      const currentRole = targetMember?.role;
      
      // 이미 Staff 이상의 역할을 가진 사용자는 바로 역할 변경
      const isAlreadyStaff = ['STAFF', 'LEADER', 'PRESIDENT', 'ADMIN'].includes(currentRole);
      
      let response;
      if (isAlreadyStaff) {
        // 기존 스태프/관리자의 역할 변경 (온보딩 생략)
        response = await axios.post(`/api/admin/users/role?userId=${assignModal.userId}&role=${assignRole}`);
        alert(`Role successfully changed to ${assignRole}.`);
      } else {
        // 새로운 스태프 온보딩 프로세스
        response = await axios.post('/api/admin/users/assign-staff', {
          userId: assignModal.userId,
          targetRole: assignRole,
          reason: `Assigned as ${assignRole} by admin`,
          branch: assignBranch // 사용자가 선택한 지부
        });
        
        if (response.data.success) {
          alert(response.data.message);
        } else {
          alert(response.data.message);
          return;
        }
      }
      
      setAssignModal({ open: false, userId: null, email: '', currentRole: null });
      // 화면을 새로고침하여 최신 상태 반영
      const res = await axios.get('/api/admin/users');
      setMembers(res.data);
      
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.response?.data || e.message;
      const actionType = ['STAFF', 'LEADER', 'PRESIDENT', 'ADMIN'].includes(
        filteredMembers.find(m => m.id === assignModal.userId)?.role
      ) ? 'Role change' : 'Staff assignment';
      alert(`${actionType} failed: ` + errorMessage);
    }
  };


  // ✅ 멤버십 삭제 함수
  const handleDeleteMembership = async (userId, branchName) => {
    try {
      await axios.delete(`/api/admin/users/memberships?userId=${userId}&branchName=${branchName}`);
      alert('Membership deleted successfully');
      // 목록 새로고침
      const response = await axios.get('/api/admin/users');
      setMembers(response.data);
      setSelectedUser(null); // 모달 닫기
    } catch (error) {
      alert('Failed to delete membership');
    }
  };



  return (
    <div className="p-4 sm:p-8">
      {/* 사용자 상세 정보 모달 */}
      {selectedUser && (
        <UserDetailModal 
          user={selectedUser} 
          onClose={() => setSelectedUser(null)}
          onDeleteMembership={handleDeleteMembership}
          currentUser={user}
        />
      )}
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-800">Member Info</h1>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {canShowBulkAssign && selectedUsers.size > 0 && (
            <div className="text-sm text-gray-600">
              <span className="text-blue-600 font-semibold">선택: {selectedUsers.size}</span>
            </div>
          )}
          <div className="flex gap-2">
            {canShowBulkAssign && selectedUsers.size > 0 && (
              <>
                <button 
                  onClick={() => setShowBulkAssign(true)} 
                  className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  Bulk Assign ({selectedUsers.size})
                </button>
                <button 
                  onClick={clearSelection} 
                  className="px-3 py-2 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                >
                  Clear
                </button>
              </>
            )}
            <button onClick={exportXLSX} className="px-3 py-2 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600 transition-all duration-200 focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 shadow-sm font-medium">Export XLSX</button>
          </div>
        </div>
      </div>

      {/* Branch Tabs - Enhanced UI */}
      <div className={`mb-6 ${isMobile ? '' : 'overflow-x-auto'}`}>
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border-2 border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Filter by Branch:</span>
          </div>
          <div className={`flex gap-3 ${isMobile ? 'flex-wrap' : 'min-w-max'}`}>
            {branches.map(b => (
              <button
                key={b}
                onClick={() => setBranchFilter(b)}
                className={
                  (branchFilter === b
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg scale-105 border-2 border-blue-500 '
                    : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md border-2 border-gray-300 hover:border-blue-300 ')
                  + `px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${isMobile ? 'flex-1 min-w-0' : 'whitespace-nowrap'}`
                }
                title={b === 'ALL' ? 'Show all branches' : `Show ${b} branch only`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="font-bold">{b === 'ALL' ? '🌐 ALL' : `📍 ${b}`}</span>
                  <span className={
                    (branchFilter === b 
                      ? 'bg-white text-blue-700 font-bold shadow-sm' 
                      : 'bg-blue-100 text-blue-700 font-semibold')
                    + ' px-2.5 py-1 rounded-full text-xs min-w-[2rem] text-center'}>
                    {b === 'ALL' ? members.length : (branchCounts[b] || 0)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {/* Controls */}
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 border-b">
                        <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search: Name/Email"
            className="border rounded px-3 py-2"
          />
          <select value={roleFilter} onChange={e => handleRoleFilterChange(e.target.value)} className="border rounded px-3 py-2">
            {roleOptions.map(r => (
              <option key={r} value={r}>
                {r === 'ALL' ? 'Filter: Role' : 
                 r === 'STAFF' ? 'ALL STAFF (President/Leader/Staff)' : 
                 r === 'ADMIN' ? 'ADMIN (System-wide)' : r}
              </option>
            ))}
          </select>
          <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)} className="border rounded px-3 py-2">
            {branches.map(b => (
              <option key={b} value={b}>{b === 'ALL' ? 'Filter: Branch' : b}</option>
            ))}
          </select>
          <select value={nationalityFilter} onChange={e => setNationalityFilter(e.target.value)} className="border rounded px-3 py-2">
            {nationalities.map(n => (
              <option key={n} value={n}>
                {n === 'ALL' ? 'Filter: Nationality' : n === 'NON_TAIWAN' ? 'Non-Taiwan' : n}
              </option>
            ))}
          </select>
          {/* Status 필터 제거됨 */}
          <select value={sortKey} onChange={e => setSortKey(e.target.value)} className="border rounded px-3 py-2">
            <option value="name">Sort: Name</option>
            <option value="email">Sort: Email</option>
            <option value="role">Sort: Role</option>
            <option value="branch">Sort: Branch</option>
            <option value="joinDate">Sort: Join Date (Newest)</option>
            <option value="joinDateOldest">Sort: Join Date (Oldest)</option>
          </select>
          <button 
            onClick={handleClearFilters}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 font-medium transition-colors"
          >
            Clear All
          </button>
        </div>

        {/* Role summary - 클릭 가능한 필터 태그 */}
        <div className="px-4 py-3 text-sm text-gray-600 flex flex-wrap gap-4 border-b">
          <span 
            className="text-blue-600 font-semibold cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-colors"
            onClick={() => handleRoleTagClick('ALL_STAFF')}
            title="Click to filter ALL STAFF"
          >
            ALL STAFF: <b>{roleCounts.ALL_STAFF}</b>
            {branchFilter !== 'ALL' && <span className="text-xs text-blue-500 ml-1">({branchFilter})</span>}
          </span>
          {branchFilter === 'ALL' && roleCounts.ADMIN > 0 && (
            <span 
              className="text-red-600 font-medium cursor-pointer hover:bg-red-50 px-2 py-1 rounded transition-colors"
              onClick={() => handleRoleTagClick('ADMIN')}
              title="Click to filter ADMIN (Only visible in ALL)"
            >
              ADMIN: <b>{roleCounts.ADMIN}</b> <span className="text-xs text-red-500">(ALL only)</span>
            </span>
          )}
          <span 
            className="cursor-pointer hover:bg-gray-50 px-2 py-1 rounded transition-colors"
            onClick={() => handleRoleTagClick('PRESIDENT')}
            title="Click to filter PRESIDENT"
          >
            PRESIDENT: <b>{roleCounts.PRESIDENT}</b>
            {branchFilter !== 'ALL' && <span className="text-xs text-gray-500 ml-1">({branchFilter})</span>}
          </span>
          <span 
            className="text-purple-600 cursor-pointer hover:bg-purple-50 px-2 py-1 rounded transition-colors"
            onClick={() => handleTeamFilter('GA')}
            title="Click to filter GA team members"
          >
            GA: <b>{roleCounts.GA}</b>
            {branchFilter !== 'ALL' && <span className="text-xs text-purple-500 ml-1">({branchFilter})</span>}
          </span>
          <span 
            className="text-green-600 cursor-pointer hover:bg-green-50 px-2 py-1 rounded transition-colors"
            onClick={() => handleTeamFilter('PR')}
            title="Click to filter PR team members"
          >
            PR: <b>{roleCounts.PR}</b>
            {branchFilter !== 'ALL' && <span className="text-xs text-green-500 ml-1">({branchFilter})</span>}
          </span>
          <span 
            className="text-orange-600 cursor-pointer hover:bg-orange-50 px-2 py-1 rounded transition-colors"
            onClick={() => handleTeamFilter('EP')}
            title="Click to filter EP team members"
          >
            EP: <b>{roleCounts.EP}</b>
            {branchFilter !== 'ALL' && <span className="text-xs text-orange-500 ml-1">({branchFilter})</span>}
          </span>
        </div>

        {/* Status summary */}
        <div className="px-4 py-3 text-sm flex flex-wrap gap-4 border-b">
          <span 
            className="text-slate-600 font-medium cursor-pointer hover:bg-slate-50 px-2 py-1 rounded transition-colors"
            onClick={() => handleRoleTagClick('NON_MEMBER')}
            title="Click to filter Non-Members (Always shows ALL branches)"
          >
            Non-Member: <b className="text-slate-700">{statusCounts.NON_MEMBER}</b> <span className="text-xs text-gray-400">(ALL)</span>
          </span>
          <span 
            className="text-emerald-600 font-medium cursor-pointer hover:bg-emerald-50 px-2 py-1 rounded transition-colors"
            onClick={() => handleRoleTagClick('ACTIVE_MEMBER')}
            title="Click to filter Active Members"
          >
            Active Members: <b className="text-emerald-700">{statusCounts.ACTIVE_MEMBER}</b>
            {branchFilter !== 'ALL' && <span className="text-xs text-emerald-500 ml-1">({branchFilter})</span>}
          </span>
          <span 
            className="text-rose-600 font-medium cursor-pointer hover:bg-rose-50 px-2 py-1 rounded transition-colors"
            onClick={() => handleRoleTagClick('EXPIRED_MEMBER')}
            title="Click to filter Expired Members"
          >
            Expired Members: <b className="text-rose-700">{statusCounts.EXPIRED_MEMBER}</b>
            {branchFilter !== 'ALL' && <span className="text-xs text-rose-500 ml-1">({branchFilter})</span>}
          </span>
        </div>

        {/* Results Summary */}
        <div className="px-4 py-2 text-sm text-gray-600 border-b">
          {filteredMembers.length === members.length ? (
            `Showing all ${filteredMembers.length} members`
          ) : (
            `Showing ${filteredMembers.length} of ${members.length} members`
          )}
          {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
        </div>

        {loading ? (
          <p className="p-6 text-gray-500">Loading...</p>
        ) : isMobile ? (
          // Mobile Card View
          <div className="p-4 space-y-4">
            {paginatedMembers.map(member => (
              <div key={member.id} className="bg-gray-50 p-4 rounded-lg border">
                <div className="flex justify-between items-start mb-3">
                  <div 
                    className="flex-1 cursor-pointer" 
                    onClick={() => setSelectedUser(member)}
                  >
                    <h3 className="font-semibold text-gray-900 hover:text-blue-600">{member.name}</h3>
                    <p className="text-sm text-gray-600 hover:text-blue-500">{member.email}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColorClass(member.role)}`}>
                    {member.role}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mb-3 space-y-1">
                  <div><span className="font-medium">Branch:</span> {getActiveBranches(member).join(', ') || '-'}</div>
                  <div><span className="font-medium">Nationality:</span> {member.nationality === 'N/A' || !member.nationality ? 'Taiwan' : member.nationality}</div>
                  <div><span className="font-medium">Joined:</span> {member.createdAt ? new Date(member.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown'}</div>
                </div>
                {(() => {
                  const canShow = shouldShowAssignButton(user?.role, member.role);
                  const isNotSelf = user?.id !== member.id;
                  const assignableRoles = getAssignableRoles(user?.role, member.role);
                  const hasRoles = assignableRoles.length > 0;
                  
                  return canShow && isNotSelf && hasRoles && (
                    <button 
                      onClick={() => openAssign(member)} 
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Assign Role
                    </button>
                  );
                })()}
              </div>
            ))}
          </div>
        ) : (
          // Desktop Table View
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {canShowBulkAssign && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <input
                      type="checkbox"
                      checked={selectedUsers.size === filteredMembers.length && filteredMembers.length > 0}
                      onChange={handleSelectAll}
                      className="rounded"
                    />
                  </th>
                )}
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Name
                    <span className="text-gray-400">{getSortIcon('name')}</span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-1">
                    Email
                    <span className="text-gray-400">{getSortIcon('email')}</span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('role')}
                >
                  <div className="flex items-center gap-1">
                    Role
                    <span className="text-gray-400">{getSortIcon('role')}</span>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setShowRoleInfo(true); }}
                      className="ml-1 text-blue-500 hover:text-blue-700 text-sm"
                      title="Role 설명 보기"
                    >
                      ℹ️
                    </button>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('branch')}
                >
                  <div className="flex items-center gap-1">
                    Branch
                    <span className="text-gray-400">{getSortIcon('branch')}</span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('nationality')}
                >
                  <div className="flex items-center gap-1">
                    Nationality
                    <span className="text-gray-400">{getSortIcon('nationality')}</span>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('joinDate')}
                >
                  <div className="flex items-center gap-1">
                    Join Date
                    <span className="text-gray-400">{getSortIcon('joinDate')}</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedMembers.map(member => (
                <tr key={member.id}>
                  {canShowBulkAssign && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(member.id)}
                        onChange={() => handleSelectUser(member.id)}
                        className="rounded"
                      />
                    </td>
                  )}
                  <td 
                    className="px-6 py-4 whitespace-nowrap cursor-pointer hover:text-blue-600 font-medium"
                    onClick={() => setSelectedUser(member)}
                  >
                    {member.name}
                  </td>
                  <td 
                    className="px-6 py-4 whitespace-nowrap cursor-pointer hover:text-blue-600"
                    onClick={() => setSelectedUser(member)}
                  >
                    {member.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span 
                      className={`px-2 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${getRoleColorClass(member.role)}`}
                      onClick={() => setSelectedUserDetail(member)}
                      title="Click for details"
                    >
                      {member.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{getActiveBranches(member).join(', ') || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{member.nationality === 'N/A' || !member.nationality ? 'Taiwan' : member.nationality}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {member.createdAt ? new Date(member.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {(() => {
                      const canShow = shouldShowAssignButton(user?.role, member.role);
                      const isNotSelf = user?.id !== member.id;
                      const assignableRoles = getAssignableRoles(user?.role, member.role);
                      const hasRoles = assignableRoles.length > 0;
                      
                      return canShow && isNotSelf && hasRoles && (
                        <button onClick={() => openAssign(member)} className="text-blue-600 hover:underline">Assign Role</button>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {filteredMembers.length > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-lg shadow">
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select 
                value={pageSize} 
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>

            {/* Page Info */}
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredMembers.length)} of {filteredMembers.length} members
            </div>

            {/* Page Numbers */}
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded text-sm ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                {(() => {
                  const pageNumbers = [];
                  const maxVisiblePages = isMobile ? 3 : 7;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                  
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  // First page + ellipsis
                  if (startPage > 1) {
                    pageNumbers.push(
                      <button
                        key={1}
                        onClick={() => handlePageChange(1)}
                        className="px-3 py-1 rounded text-sm bg-gray-200 text-gray-700 hover:bg-gray-300"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pageNumbers.push(<span key="ellipsis1" className="px-2 text-gray-500">...</span>);
                    }
                  }

                  // Visible page numbers
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`px-3 py-1 rounded text-sm ${
                          i === currentPage
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Last page + ellipsis
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pageNumbers.push(<span key="ellipsis2" className="px-2 text-gray-500">...</span>);
                    }
                    pageNumbers.push(
                      <button
                        key={totalPages}
                        onClick={() => handlePageChange(totalPages)}
                        className="px-3 py-1 rounded text-sm bg-gray-200 text-gray-700 hover:bg-gray-300"
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pageNumbers;
                })()}

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded text-sm ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {assignModal.open && (() => {
        const targetMember = filteredMembers.find(m => m.id === assignModal.userId);
        const currentRole = targetMember?.role;
        const isAlreadyStaff = ['STAFF', 'LEADER', 'PRESIDENT', 'ADMIN'].includes(currentRole);
        const modalTitle = isAlreadyStaff ? 'Change Role' : 'Assign Staff';
        const modalDescription = isAlreadyStaff 
          ? `Change the role for ${assignModal.email}. (Current: ${currentRole})`
          : `Assign ${assignModal.email} as staff. An onboarding email will be sent.`;
        
        const availableRoles = getAssignableRoles(user?.role, currentRole);
        
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">{modalTitle}</h2>
              <p className="text-sm text-gray-600 mb-4">{modalDescription}</p>
              
              {/* 지부 선택 (첫 번째) - GUEST로 변경하는 경우가 아닐 때만 표시 */}
              {assignRole !== 'GUEST' && (
                <>
                  <label className="block text-sm mb-1 font-medium">Branch</label>
                  <select value={assignBranch} onChange={e => setAssignBranch(e.target.value)} className="w-full border rounded p-2 mb-4">
                    <option value="TAIPEI">TAIPEI</option>
                    <option value="NCCU">NCCU</option>
                    <option value="NTU">NTU</option>
                  </select>
                </>
              )}
              
              {/* 역할 선택 (두 번째) */}
              <label className="block text-sm mb-1 font-medium">New Role</label>
              <select value={assignRole} onChange={e => setAssignRole(e.target.value)} className="w-full border rounded p-2 mb-6">
                {availableRoles.map(role => (
                  <option key={role} value={role}>
                    {role === 'GUEST' ? 'Guest (Quit Staff)' : role}
                  </option>
                ))}
              </select>
              {isAlreadyStaff && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-sm text-blue-800">
                  💡 Existing staff member - role will be changed immediately. (Onboarding process skipped)
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button onClick={() => setAssignModal({ open: false, userId: null, email: '', currentRole: null })} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                <button onClick={submitAssign} className="px-4 py-2 bg-blue-600 text-white rounded">
                  {isAlreadyStaff ? 'Change Role' : 'Send Invitation'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      
      {/* ✅ 지부 선택 모달 */}
      {branchSelectionModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">⚠️ Multiple Branch Membership Detected</h2>
            <p className="text-sm text-gray-600 mb-4">
              User: <strong>{branchSelectionModal.userName}</strong>
            </p>
            
            <div className="mb-4">
              <p className="text-sm font-medium mb-2">Current Memberships:</p>
              <div className="space-y-2">
                {branchSelectionModal.userMemberships.map((membership, index) => (
                  <div key={index} className="flex items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm">✅ {membership.branchName} Staff (Active)</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm font-medium mb-2">Which branch(es) would you like to quit from?</p>
              <div className="space-y-2">
                {branchSelectionModal.userMemberships.map((membership, index) => (
                  <label key={index} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedBranches.includes(membership.branchName)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedBranches([...selectedBranches, membership.branchName]);
                        } else {
                          setSelectedBranches(selectedBranches.filter(b => b !== membership.branchName));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{membership.branchName}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setBranchSelectionModal({ open: false, userId: null, userName: '', userMemberships: [] })}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button 
                onClick={confirmBranchSelection}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm Quit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role 정보 모달 */}
      {showRoleInfo && <RoleInfoModal />}
      
      {/* 사용자 상세 정보 모달 */}
      {selectedUserDetail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">👤 User Details</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img 
                  src={selectedUserDetail.profileImage || '/default_profile.jpg'} 
                  alt="Profile" 
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <h3 className="font-medium">{selectedUserDetail.name}</h3>
                  <p className="text-sm text-gray-600">{selectedUserDetail.email}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColorClass(selectedUserDetail.role)}`}>
                    {selectedUserDetail.role}
                  </span>
                  <span className="text-sm font-medium">Role Details</span>
                </div>
                
                {(() => {
                  const role = selectedUserDetail.role;
                  const activeBranches = getActiveBranches(selectedUserDetail);
                  
                  if (role === 'ADMIN') {
                    return (
                      <div className="bg-red-50 p-3 rounded text-sm">
                        <p className="font-medium text-red-800">System Administrator</p>
                        <p className="text-red-600">Full system access and control</p>
                      </div>
                    );
                  }
                  
                  if (role === 'PRESIDENT') {
                    return (
                      <div className="bg-purple-50 p-3 rounded text-sm">
                        <p className="font-medium text-purple-800">Branch President</p>
                        <p className="text-purple-600">Branch: {activeBranches.join(', ') || 'Not specified'}</p>
                        <p className="text-purple-600 mt-1">Can assign staff roles and manage branch operations</p>
                      </div>
                    );
                  }
                  
                  if (role === 'LEADER') {
                    return (
                      <div className="bg-blue-50 p-3 rounded text-sm">
                        <p className="font-medium text-blue-800">Team Leader</p>
                        <p className="text-blue-600">Department: {getUserDepartment(selectedUserDetail)}</p>
                        <p className="text-blue-600">Branch: {activeBranches.join(', ') || 'Not specified'}</p>
                        <p className="text-blue-600 mt-1">Can assign staff and member roles</p>
                      </div>
                    );
                  }
                  
                  if (role === 'STAFF') {
                    return (
                      <div className="bg-green-50 p-3 rounded text-sm">
                        <p className="font-medium text-green-800">Staff Member</p>
                        <p className="text-green-600">Department: {getUserDepartment(selectedUserDetail)}</p>
                        <p className="text-green-600">Branch: {activeBranches.join(', ') || 'Not specified'}</p>
                        <p className="text-green-600 mt-1">Active staff with event management access</p>
                      </div>
                    );
                  }
                  
                  if (role === 'MEMBER') {
                    return (
                      <div className="bg-orange-50 p-3 rounded text-sm">
                        <p className="font-medium text-orange-800">Active Member</p>
                        <p className="text-orange-600">Branch: {activeBranches.join(', ') || 'Not specified'}</p>
                        <p className="text-orange-600 mt-1">Can participate in events and activities</p>
                      </div>
                    );
                  }
                  
                  if (role === 'GUEST') {
                    const status = selectedUserDetail.status || 'NON_MEMBER';
                    const previousRole = selectedUserDetail.previousRole;
                    return (
                      <div className="bg-gray-50 p-3 rounded text-sm">
                        <p className="font-medium text-gray-800">Non-Member User</p>
                        <p className="text-gray-600">Status: {status}</p>
                        {previousRole && (
                          <p className="text-gray-600 mt-1">
                            <span className="font-medium">Previous Role:</span> {previousRole}
                          </p>
                        )}
                        {status === 'EXPIRED_MEMBER' && (
                          <p className="text-gray-600 mt-1">Previous membership has expired</p>
                        )}
                        {status === 'NON_MEMBER' && (
                          <p className="text-gray-600 mt-1">No active membership - can apply to join</p>
                        )}
                      </div>
                    );
                  }
                  
                  return null;
                })()}
                
                {selectedUserDetail.nationality && (
                  <div className="mt-3 text-sm">
                    <span className="font-medium">Nationality:</span> {selectedUserDetail.nationality === 'N/A' || !selectedUserDetail.nationality ? 'Taiwan' : selectedUserDetail.nationality}
                  </div>
                )}
                
                {selectedUserDetail.createdAt && (
                  <div className="text-sm">
                    <span className="font-medium">Joined:</span> {new Date(selectedUserDetail.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                )}
              </div>
            </div>
            
            <button 
              onClick={() => setSelectedUserDetail(null)}
              className="mt-6 w-full px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
      
      {/* Bulk Assign 모달 */}
      {showBulkAssign && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Bulk Assign Roles</h2>
            <p className="text-sm text-gray-600 mb-4">
              Assign roles to {selectedUsers.size} selected users.
            </p>
            
            {/* Role 선택 */}
            <label className="block text-sm mb-1 font-medium">New Role</label>
            <select 
              value={bulkAssignRole} 
              onChange={e => setBulkAssignRole(e.target.value)} 
              className="w-full border rounded p-2 mb-4"
            >
              {getAssignableRoles(user?.role).map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
            
            {/* Branch 선택 (GUEST가 아닌 경우만) */}
            {bulkAssignRole !== 'GUEST' && (
              <>
                <label className="block text-sm mb-1 font-medium">Branch</label>
                <select 
                  value={bulkAssignBranch} 
                  onChange={e => setBulkAssignBranch(e.target.value)} 
                  className="w-full border rounded p-2 mb-4"
                >
                  <option value="TAIPEI">TAIPEI</option>
                  <option value="NCCU">NCCU</option>
                  <option value="NTU">NTU</option>
                </select>
              </>
            )}
            
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4 text-sm text-yellow-800">
              ⚠️ This will assign the selected role to all {selectedUsers.size} users. This action cannot be undone.
            </div>
            
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setShowBulkAssign(false)} 
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button 
                onClick={handleBulkAssign} 
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Assign Roles
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
